(() => {
  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  // 1. TYPO & SYNONYM DICTIONARY
  const autocorrect = {
    'earing': 'earring', 'earings': 'earrings',
    'braclet': 'bracelet', 'bracelete': 'bracelet', 'bracelets': 'bracelet',
    'neclace': 'necklace', 'neckless': 'necklace',
    'dimond': 'diamond', 'diamon': 'diamond', 'diamant': 'diamond',
    'jewerly': 'jewelry', 'jewlery': 'jewelry', 'joaillerie': 'jewelry',
    'mens': 'men', 'womens': 'women', 'ladies': 'women', 'man': 'men'
  };

  const stopWords = new Set(['a', 'an', 'the', 'and', 'or', 'for', 'with', 'in', 'of', 'to', 'my']);
  const kidsTerms = ['kid', 'kids', 'child', 'children', 'baby', 'babies', 'toddler', 'toddlers', 'enfant'];
  const mensTerms = ['man', 'men', 'mens', 'male', 'him'];
  const womensTerms = ['woman', 'women', 'womens', 'female', 'her', 'ladies', 'lady'];

  // Master Gemstone List for Strict Gemstone Isolation
  const gemstones = [
    'tanzanite', 'emerald', 'sapphire', 'ruby', 'diamond', 
    'peridot', 'garnet', 'topaz', 'amethyst', 'aquamarine', 
    'opal', 'tourmaline', 'citrine', 'morganite', 'tsavorite',
    'alexandrite', 'spinel', 'onyx', 'turquoise', 'pearl'
  ];

  const phrases = [
    'Ring', 'Rings Women', 'Rings Men', 'Engagement Rings', 'Wedding Bands', 'Signet Rings', 'Gold Rings', 'Diamond Rings', 
    'Necklace', 'Necklace Gold', 'Necklace Diamond', 'Gold Chains', 'Tennis Necklace',
    'Bracelets', 'Gold Bracelets', 'Diamond Bracelets', 'Bangles',
    'Earrings', 'Gold Earrings', 'Diamond Earrings', 'Diamond Studs', 'Hoops', 'Drop Earrings',
    "Men's Jewelry", "High Jewelry", "Luxury Jewelry"
  ];
  
  const collectionLinks = {
    'Rings Men': 'collections/mens-jewelry?beucher_type=rings',
    'Necklaces Men': 'collections/mens-jewelry?beucher_type=necklaces-pendants',
    'Bracelets Men': 'collections/mens-jewelry?beucher_type=bracelets',
    "Men's Jewelry": 'collections/mens-jewelry',
  };

  const tokens = value => normalize(value).match(/[\p{L}\p{N}-]+/gu) || [];
  
  function displayQuery(value) {
    return String(value || '').replace(/\*+$/g, '').trim();
  }

  // 2. QUERY CLEANER - PURE TEXT ONLY
  function scopedQuery(value) {
    let clean = String(value || '').replace(/[^\p{L}\p{N}\s-]/gu, ' ').trim();
    let words = clean.split(/\s+/).filter(w => !stopWords.has(normalize(w)));
    if (words.length === 0) return clean;

    words = words.map(w => autocorrect[normalize(w)] || w);
    return words.join(' ');
  }

  // 3. STOREFRONT URL CONSTRUCTOR
  function url(query, page = 1, response = false) {
    const result = new URL(`${window.Shopify?.routes?.root || '/'}search`, window.location.origin);
    const cleanQ = scopedQuery(query);
    
    result.searchParams.set('q', cleanQ);
    result.searchParams.set('type', 'product');
    result.searchParams.set('options[fields]', 'title,product_type,tag');
    result.searchParams.set('options[prefix]', 'last');
    result.searchParams.set('options[unavailable_products]', 'last');
    result.searchParams.set('sort_by', 'relevance');
    
    if (page > 1) result.searchParams.set('page', page);
    if (response) result.searchParams.set('section_id', 'beucher-search-results');
    return result;
  }

  // 4. SURGICAL ENFORCEMENT & PHRASE PURIFIER
  function rank(products, query) {
    const rawNeedle = displayQuery(query);
    const normalizedNeedle = normalize(rawNeedle);
    const needleWords = tokens(rawNeedle).map(w => autocorrect[w] || w);
    
    const clientWantsKids = kidsTerms.some(term => needleWords.includes(term));
    const clientWantsStuds = needleWords.includes('stud') || needleWords.includes('studs');
    const clientWantsHoops = needleWords.includes('hoop') || needleWords.includes('hoops');
    const clientWantsRings = needleWords.includes('ring') || needleWords.includes('rings') || needleWords.includes('band');
    const clientWantsMens = mensTerms.some(term => needleWords.includes(term));
    const clientWantsWomens = womensTerms.some(term => needleWords.includes(term));
    
    // Strict tennis necklace check
    const clientWantsTennisNecklace = normalizedNeedle.includes('tennis') && normalizedNeedle.includes('necklace');
    
    // Detect if client explicitly searched for specific gemstone(s)
    const activeGemstones = gemstones.filter(gem => needleWords.includes(gem));

    let filtered = products;

    // A. Quarantine Kids products unless queried
    if (!clientWantsKids) {
      filtered = filtered.filter(product => {
        const text = normalize([product.title, product.type, ...(product.tags || [])].join(' '));
        const words = tokens(text);
        return !kidsTerms.some(term => words.includes(term));
      });
    }

    // B. Strict Category & Phrase Rules
    filtered = filtered.filter(product => {
      const fullText = normalize([product.title, product.type, ...(product.tags || [])].join(' '));
      const words = tokens(fullText);
      const isTaggedMen = mensTerms.some(term => words.includes(term));

      // TENNIS NECKLACE PHRASE ENFORCEMENT:
      // Only keep items explicitly containing "tennis necklace" or "tennis line necklace"
      if (clientWantsTennisNecklace) {
        const hasTennisNecklace = fullText.includes('tennis necklace') || fullText.includes('tennis line necklace');
        if (!hasTennisNecklace) return false;
      }

      // GEMSTONE ISOLATION:
      if (activeGemstones.length > 0) {
        const hasRequestedGem = activeGemstones.some(gem => words.includes(gem));
        if (!hasRequestedGem) return false;
      }

      // STRICT GENDER GATE:
      if (clientWantsMens && !isTaggedMen) return false;
      if (clientWantsWomens && isTaggedMen && !womensTerms.some(term => words.includes(term))) return false;

      // STRICT CATEGORY GATES:
      if (clientWantsStuds) {
        if (words.includes('bangle') || words.includes('bracelet') || words.includes('necklace') || words.includes('ring') || words.includes('drop')) return false;
        if (words.includes('studded') && !needleWords.includes('studded')) return false;
      }
      if (clientWantsHoops) {
        if (words.includes('stud') || words.includes('drop') || words.includes('ring')) return false;
      }
      if (clientWantsRings && !needleWords.includes('earring') && !needleWords.includes('necklace')) {
        if (words.includes('earring') || words.includes('stud') || words.includes('hoop') || words.includes('necklace')) return false;
      }

      return true;
    });

    // C. Prioritized Scoring
    return filtered.map((product, index) => {
      let score = 0;
      const text = normalize([product.title, product.type, ...(product.tags || [])].join(' '));
      const normalizedTitle = normalize(product.title);
      
      if (normalizedTitle.includes(needleWords.join(' '))) score += 500;
      if (clientWantsTennisNecklace && normalizedTitle.includes('tennis necklace')) score += 300;
      if (clientWantsStuds && text.includes('diamond')) score += 250;
      if (text.includes('chatelaillon') || text.includes('high jewelry')) score += 50;
      
      return { product, index, score };
    })
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(item => item.product);
  }

  // 5. DATA FETCHING (Drawer / Autocomplete)
  const cache = new Map();
  async function fetchResults(query, page = 1, signal) {
    const scoped = scopedQuery(query);
    if (!scoped) return { count: 0, pages: 0, page: 1, products: [] };
    
    const endpoint = url(query, page, true).href;
    const cached = cache.get(endpoint);
    if (cached && Date.now() - cached.time < 30000) return cached.data;
    
    const response = await fetch(endpoint, { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Search request failed (${response.status})`);
    
    const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
    const payload = markup.querySelector('[data-beucher-search-payload]');
    if (!payload) throw new Error('Search response is missing');
    
    const data = JSON.parse(payload.textContent);
    data.products = rank(data.products, query);
    
    if (cache.size >= 20) cache.delete(cache.keys().next().value);
    cache.set(endpoint, { time: Date.now(), data });
    return data;
  }

  function link(label) {
    const path = collectionLinks[label];
    return path ? new URL(`${window.Shopify?.routes?.root || '/'}${path}`, window.location.origin).href : url(label).href;
  }

  function suggestions(query) {
    const words = tokens(query).filter(w => !stopWords.has(w));
    if (!words.length) return [];
    return phrases.filter(phrase => {
      const candidate = tokens(phrase);
      return words.every(word => candidate.some(term => term.startsWith(word)));
    }).slice(0, 6);
  }

  function highlight(label, query) {
    const cleanQuery = displayQuery(query);
    const words = tokens(cleanQuery).sort((a, b) => b.length - a.length);
    return label.split(/(\s+|-)/).map(part => { 
      if (part === '-' || part.trim() === '') return escape(part);
      const word = words.find(word => normalize(part).startsWith(word));
      return word ? `<strong>${escape(part.slice(0, word.length))}</strong>${escape(part.slice(word.length))}` : escape(part);
    }).join('');
  }

  function imageMarkup(picture, lazy = false, menu = false) {
    if (!picture) return '';
    return `<img ${lazy ? 'data-src' : 'src'}="${escape(picture.src)}" ${lazy ? 'data-srcset' : 'srcset'}="${escape(picture.small)} 300w, ${escape(picture.src)} 600w" sizes="${menu ? '(min-width: 990px) 25vw, 50vw' : '(max-width: 749px) 30vw, 20vw'}" width="${Number(picture.width) || 600}" height="${Number(picture.height) || 600}" alt="${escape(picture.alt)}" loading="lazy" decoding="async" draggable="false">`;
  }
  
  window.BeucherSearch = { escape, displayQuery, scopedQuery, url, link, collectionLinks, suggestions, highlight, rank, fetchResults, imageMarkup };
})();